import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  uploadCourses,
  resetStatus as resetCourseStatus,
} from "../../Redux/CourseDetails";
import {
  uploadLearnersReport,
  resetStatus as resetLearnerStatus,
} from "../../Redux/LearnersReport";
import {
  uploadUserData,
  resetStatus as resetUserStatus,
} from "../../Redux/UserDetails";
import "./Admin.css";

const Admin = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const courseState = useSelector((state) => state.courseDetails);
  const learnerState = useSelector((state) => state.learnersReport);
  const userState = useSelector((state) => state.userDetails);

  const [activeTab, setActiveTab] = useState("courses");
  const [selectedHub, setSelectedHub] = useState("");
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState("");
  const [file, setFile] = useState(null);
  const [uiError, setUiError] = useState("");
  const [uiSuccess, setUiSuccess] = useState("");

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const loader =
    activeTab === "courses"
      ? courseState?.loader
      : activeTab === "learners"
        ? learnerState?.loader
        : userState?.loader;

  const error =
    activeTab === "courses"
      ? courseState?.error
      : activeTab === "learners"
        ? learnerState?.error
        : userState?.error;

  const validateBusinessUnit = () => {
    if (!selectedBusinessUnit) {
      setUiError("Please select a business unit.");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    setUiError("");
    setUiSuccess("");

    if (!file) {
      setUiError("Please select an Excel file.");
      return;
    }

    if (activeTab === "courses") {
      if (!selectedHub) {
        setUiError("Please select a hub.");
        return;
      }

      if (!validateBusinessUnit()) return;

      setHasSubmitted(true);
      dispatch(uploadCourses(selectedHub, selectedBusinessUnit, file));
    } else if (activeTab === "learners") {
      if (!validateBusinessUnit()) return;

      setHasSubmitted(true);
      dispatch(uploadLearnersReport(selectedBusinessUnit, file));
    } else if (activeTab === "users") {
      if (!validateBusinessUnit()) return;

      setHasSubmitted(true);
      dispatch(uploadUserData(selectedBusinessUnit, file));
    }
  };

  const currentState =
    activeTab === "courses"
      ? courseState
      : activeTab === "learners"
        ? learnerState
        : userState;

  useEffect(() => {
    if (currentState.loader) return;

    if (currentState.error) {
      setUiError(currentState.error);
      return;
    }

    if (currentState.success) {
      setUiSuccess(currentState.success);

      // ✅ reset form
      setFile(null);
      setSelectedHub("");
      setSelectedBusinessUnit("");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [currentState.loader, currentState.error, currentState.success]);

  // Auto-clear messages (unchanged)
  useEffect(() => {
    if (!uiError && !uiSuccess) return;

    const timer = setTimeout(() => {
      setUiError("");
      setUiSuccess("");

      if (activeTab === "courses") {
        dispatch(resetCourseStatus());
      } else if (activeTab === "learners") {
        dispatch(resetLearnerStatus());
      } else {
        dispatch(resetUserStatus());
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [uiError, uiSuccess, activeTab, dispatch]);

  // Clear messages on input change (unchanged)
  useEffect(() => {
    if (!uiError && !uiSuccess) return;

    if (file || selectedHub || selectedBusinessUnit) {
      setUiError("");
      setUiSuccess("");
    }
  }, [file, selectedHub, selectedBusinessUnit, uiError, uiSuccess]);

  const isBuInvalid = hasSubmitted && !selectedBusinessUnit;

  return (
    <div className="upload-wrapper">
      {/* LEFT TABS */}
      <div className="upload-tabs">
        <div
          className={`tab ${activeTab === "courses" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("courses");
            setSelectedBusinessUnit("");
            setFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
            setUiError("");
            setUiSuccess("");
          }}
        >
          Upload Courses
        </div>

        <div
          className={`tab ${activeTab === "learners" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("learners");
            setSelectedBusinessUnit("");
            setSelectedHub("");
            setFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
            setUiError("");
            setUiSuccess("");
          }}
        >
          Upload Learners Report
        </div>

        <div
          className={`tab ${activeTab === "users" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("users");
            setSelectedBusinessUnit("");
            setSelectedHub("");
            setFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
            setUiError("");
            setUiSuccess("");
          }}
        >
          Upload User Data
        </div>
      </div>

      {/* CONTENT */}
      <div className="upload-content">
        <h2>
          {activeTab === "courses"
            ? "Upload Courses File"
            : activeTab === "learners"
              ? "Upload Learners Report File"
              : "Upload User Data"}
        </h2>

        {/* HUB SELECT */}
        {activeTab === "courses" && (
          <>
            {/* NEW BUSINESS UNIT DROPDOWN */}
            <div className="form-row">
              <label>Select Business Unit :</label>

              <select
                value={selectedBusinessUnit}
                onChange={(e) => setSelectedBusinessUnit(e.target.value)}
                disabled={loader}
                className={isBuInvalid ? "input-error" : ""}
              >
                <option value="" disabled>
                  Select Business Unit
                </option>

                <option value="Song">Song</option>
                <option value="Salesforce">Salesforce</option>
              </select>
            </div>

            <div className="form-row">
              <label>Select the hub to upload file :</label>

              <select
                value={selectedHub}
                onChange={(e) => setSelectedHub(e.target.value)}
                disabled={loader}
              >
                <option value="" disabled>
                  Select Hub
                </option>

                <option value="Foundation">Core Hub</option>
                <option value="GenAI and Agentic AI Hub">
                  GenAI and Agentic AI Hub
                </option>
                <option value="Delivery Modernisation Hub">
                  Delivery Modernisation Hub
                </option>
                <option value="Future of Experience Hub">
                  Future of Experience Hub
                </option>
                <option value="Sales & Solutioning Hub">
                  Sales & Solutioning Hub
                </option>
                <option value="Product Hub">Product Hub</option>
              </select>
            </div>
          </>
        )}

        {/* BUSINESS UNIT SELECT */}
        {(activeTab === "users" || activeTab === "learners") && (
          <div className="form-row">
            <label>Select Business Unit :</label>

            <select
              value={selectedBusinessUnit}
              onChange={(e) => setSelectedBusinessUnit(e.target.value)}
              disabled={loader}
              className={isBuInvalid ? "input-error" : ""}
            >
              <option value="" disabled>
                Select Business Unit
              </option>

              <option value="Song">Song</option>
              <option value="Salesforce">Salesforce</option>
            </select>
          </div>
        )}

        {/* FILE INPUT */}
        <div className="form-row">
          <label>Select File:</label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            disabled={loader}
            onChange={(e) => setFile(e.target.files[0])}
          />
          <span className="file-name">
            {file ? file.name : "No File Selected"}
          </span>
        </div>

        <button className="submit-btn" onClick={handleSubmit} disabled={loader}>
          {loader ? "Uploading..." : "SUBMIT"}
        </button>

        {/* UI ERROR */}
        {uiError && <div className="error-msg">{uiError}</div>}

        {/* UI SUCCESS */}
        {uiSuccess && <div className="success-msg">{uiSuccess}</div>}
      </div>
    </div>
  );
};

export default Admin;
